// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState } from 'react';
import { array, string, bool, object } from 'prop-types';
import { useTranslation } from 'react-i18next';

import useToastNotifications from 'Hooks/useToastNotifications';
import useCart from 'Hooks/useCart';
import useUser from 'Hooks/useUser';
import useModals from 'Hooks/useModals';
import ExternalSuppliersComponent from 'Components/Product/ExternalSuppliers';
import useAnalytics from 'Hooks/useAnalytics';

const ExternalSuppliers = memo(({
  mainProduct,
  products,
  currency,
  withVat
}) => {
  const { data: cart, updateProduct } = useCart();
  const { getUserData } = useUser();
  const toast = useToastNotifications();
  const { t } = useTranslation();
  const modalContext = useModals();
  const analytics = useAnalytics();
  const [productsState, setProductsState] = useState([]);
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const userCheckoutData = getUserData('checkout') || {};

  useEffect(() => {
    if (products && products.length) {
      setProductsState(products.map(prod => ({
        ...prod,
        productId: mainProduct.id || mainProduct.productId,
        quantity: 1,
        hasPrice: false,
      })));
    }
  }, [products, mainProduct]);

  const addProductToCart = async product => {
    if (userCheckoutData.dropShipment) {
      toast.addNotification({
        type: 'warning',
        title: t('products.dropShipmentWarning')
      });
      return;
    }
    setAddToCartLoading(product.id);
    try {
      const payload = {
        id: product.productId,
        quantity: product.quantity,
        minQuantity: 1,
        supplierId: product.supplierId,
        supplierOfferId: product.id,
      };

      const existingProduct = cart.items.find(item => (
        item.productId === product.productId
        && item?.supplierId === product.supplierId
        // && item?.supplierOfferId === product.supplierOfferId
      ));

      if ((existingProduct?.quantity || 0) + payload.quantity > product.stock) {
        toast.addNotification({
          type: 'warning',
          title: t('products.insufficientStock')
        });
        return;
      }

      if (existingProduct) {
        payload.quantity += existingProduct.quantity;
      }

      await updateProduct(payload);
      setProductsState(prods => prods.map(prod => {
        if (prod.id === product.id) {
          return {
            ...prod,
            quantity: 1
          };
        }
        return prod;
      }));
      toast.addNotification({
        title: t('cart.successfullyAddedToCart')
      });
    } catch (err) {
      toast.addNotification({
        type: 'error',
        title: t('cart.failedAddToCart'),
        message: err?.info?.errors?.at(0)?.message
      });
    } finally {
      setAddToCartLoading(false);
    }
  };

  const handleOnAddToCart = async product => {
    if (userCheckoutData.dropShipment) {
      toast.addNotification({
        type: 'warning',
        title: t('products.dropShipmentWarning')
      });
      return;
    }

    modalContext.setState('ADD_EXTERNAL_SUPPLIER_PRODUCT_TO_CART', {
      open: true,
      product: {
        ...mainProduct,
        ...product,
      },
      onConfirm: () => addProductToCart(product)
    });
  };

  const handleOnChangeQuantity = (productId, quantity) => {
    analytics.track('external_suppliers_change_quantity');
    setProductsState(prods => prods.map(prod => {
      if (prod.id === productId) {
        return {
          ...prod,
          quantity
        };
      }
      return prod;
    }));
  };

  return (
    <ExternalSuppliersComponent
      mainProduct={ mainProduct }
      products={ productsState }
      currency={ currency }
      withVat={ withVat }
      isLoading={ addToCartLoading }
      onAddToCart={ handleOnAddToCart }
      onChangeQuantity={ handleOnChangeQuantity }
    />
  );
});

ExternalSuppliers.propTypes = {
  mainProduct: object,
  products: array,
  currency: string,
  withVat: bool,
};

ExternalSuppliers.displayName = 'ExternalSuppliers';

export default ExternalSuppliers;
